"use client";

import React from "react";
import { CircleCheck as CheckCircle2, TrendingUp, Layers, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface DecisionItem {
  id: string;
  title: string;
  description: string;
}
const planningDecisions: DecisionItem[] = [{
  id: "layout",
  title: "Layout reconfiguration",
  description: "Decide whether to preserve your existing footprint or knock down walls to open the kitchen into adjoining living spaces."
}, {
  id: "cabinetry",
  title: "Cabinetry style & build",
  description: "Select between custom, semi-custom, or stock cabinetry, including inset, full overlay, painted wood, or natural stained finishes."
}, {
  id: "countertops",
  title: "Countertop material selection",
  description: "Evaluate surface durability and maintenance requirements across quartz, natural marble, quartzite, granite, or solid surface."
}, {
  id: "appliances",
  title: "Appliance choices",
  description: "Determine spatial dimensions and rough-in needs for induction cooktops, dual fuel ranges, panel-ready refrigeration, and venting hoods."
}, {
  id: "lighting",
  title: "Lighting design",
  description: "Layer task lighting under cabinets, recessed ceiling downlights, and statement island pendants on independent dimming zones."
}, {
  id: "flooring",
  title: "Flooring material",
  description: "Match kitchen moisture resistance and daily traffic demands using site-finished hardwood, engineered planks, or large-format tile."
}];
const costDrivers: DecisionItem[] = [{
  id: "square-footage",
  title: "Square footage",
  description: "Total kitchen area directly dictates raw material volumes, demolition scope, and finishing labor across surfaces."
}, {
  id: "structural-scope",
  title: "Scope of structural change",
  description: "Removing load-bearing partitions, sinking support beams, or resizing exterior windows requires engineering and framing crews."
}, {
  id: "material-grade",
  title: "Material grade",
  description: "Handcrafted tiles, exotic quartzites, and solid-wood dovetail joinery require specialized fabrication compared to standard lines."
}, {
  id: "appliance-tier",
  title: "Appliance tier",
  description: "Commercial-grade performance packages require higher electrical amperage, dedicated gas lines, and specialized make-up air venting."
}, {
  id: "relocation",
  title: "Electrical & plumbing relocation",
  description: "Relocating sink stacks, gas rough-ins, or the main electrical panel beneath subfloors adds trade-specific mechanical labor."
}, {
  id: "permits",
  title: "Permit complexity",
  description: "Municipal jurisdictional reviews, structural plan sign-offs, and staged mechanical inspections affect schedule and administration."
}];
export default function RemodelConsiderations() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="kitchenremodelconsiderations" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`max-w-3xl mb-14 lg:mb-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        opacity: isVisible ? 1 : 0
      }}>
          <div className="flex items-center gap-2 mb-3 text-primary font-medium text-sm tracking-wider uppercase">
            <Compass className="size-4" />
            <span>Essential Project Planning</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground text-balance">
            Kitchen remodel decisions and budgeting factors
          </h2>
          <p className="mt-4 text-base sm:text-lg text-foreground/80 font-sans leading-relaxed text-balance">
            Remodeling a kitchen involves deliberate choices around design, structural flow, and the physical scope that defines investment requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          {/* Column 1: Key Decisions */}
          <div className={`lg:col-span-6 transition-all duration-700 delay-100 ease-out flex flex-col gap-6 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <div className="relative aspect-16/9 w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_decisions_planning.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_decisions_planning.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_design_decisions_planning.webp-wvc-sizes" alt="Modern kitchen renovation featuring custom shaker cabinetry, marble countertops, and warm recessed pendant lighting" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>

              <Card className="border-0 shadow-none rounded-none bg-transparent pt-6">
                <CardHeader className="px-6 pb-2">
                  <div className="flex items-center gap-2.5 text-secondary">
                    <Layers className="size-5 text-primary" />
                    <CardTitle className="text-xl sm:text-2xl font-serif font-medium text-foreground">
                      Layout, material, and structural choices
                    </CardTitle>
                  </div>
                  <p className="text-sm text-foreground/75 mt-1">
                    Core architectural decisions that shape everyday culinary ergonomics and longevity.
                  </p>
                </CardHeader>

                <CardContent className="px-6 pt-4 pb-6">
                  <ul className="space-y-4">
                    {planningDecisions.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3.5 group rounded-lg p-2.5 -mx-2.5 transition-colors hover:bg-accent/40">
                        <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                        <div>
                          <h3 className="text-sm sm:text-base font-semibold text-foreground">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-foreground/75 mt-0.5 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Column 2: Cost-Driving Factors */}
          <div className={`lg:col-span-6 transition-all duration-700 delay-200 ease-out flex flex-col gap-6 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          opacity: isVisible ? 1 : 0
        }}>
            <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
              <div className="relative aspect-16/9 w-full bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_structural_rough_in_factors.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_structural_rough_in_factors.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/kitchen_structural_rough_in_factors.png-wvc-sizes" alt="Kitchen renovation construction site showing open wall studs, electrical conduits, and relocated plumbing lines" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>

              <Card className="border-0 shadow-none rounded-none bg-transparent pt-6">
                <CardHeader className="px-6 pb-2">
                  <div className="flex items-center gap-2.5 text-secondary">
                    <TrendingUp className="size-5 text-primary" />
                    <CardTitle className="text-xl sm:text-2xl font-serif font-medium text-foreground">
                      Primary cost-driving factors
                    </CardTitle>
                  </div>
                  <p className="text-sm text-foreground/75 mt-1">
                    Technical variables that influence construction hours, craftsmanship tiers, and site preparation.
                  </p>
                </CardHeader>

                <CardContent className="px-6 pt-4 pb-6">
                  <ul className="space-y-4">
                    {costDrivers.map((item, i) => <li key={item.id} data-index={i} className="flex items-start gap-3.5 group rounded-lg p-2.5 -mx-2.5 transition-colors hover:bg-accent/40">
                        <div className="size-5 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-[11px] font-mono font-medium text-secondary-foreground">{i + 1}</span>
                        </div>
                        <div>
                          <h3 className="text-sm sm:text-base font-semibold text-foreground">
                            {item.title}
                          </h3>
                          <p className="text-xs sm:text-sm text-foreground/75 mt-0.5 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}